import { themeColorPalettes } from './useThemeColors'

/**
 * Theme CSS variable generation for dynamic color theming.
 * Replaces: mixins/layout/theme.js
 *
 * Accepts a reactive getter so that CSS variables update
 * automatically when the theme or title changes at runtime.
 */

function hexToRgb(hex: string): string | null {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
  hex = hex.replace(shorthandRegex, (_, r, g, b) => r + r + g + g + b + b)

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? `${parseInt(result[1], 16)} ${parseInt(result[2], 16)} ${parseInt(result[3], 16)}`
    : null
}

interface ThemeConfig {
  light: { primary: string; gray: string }
  dark: { primary: string; gray: string }
}

function buildStyles(theme: ThemeConfig): string {
  const lightPrimary = themeColorPalettes[theme.light.primary] || themeColorPalettes.ovatugreen
  const lightGray = themeColorPalettes[theme.light.gray] || themeColorPalettes.zinc
  const darkPrimary = themeColorPalettes[theme.dark.primary] || themeColorPalettes.ovatublue
  const darkGray = themeColorPalettes[theme.dark.gray] || themeColorPalettes.zinc

  return `:root {
    ${Object.entries(lightPrimary).map(([key, value]) => `--raw-primary-${key}: ${hexToRgb(value as string)};`).join('\n')}
    ${Object.entries(lightGray).map(([key, value]) => `--raw-gray-${key}: ${hexToRgb(value as string)};`).join('\n')}
  }
  .dark {
    ${Object.entries(darkPrimary).map(([key, value]) => `--raw-primary-${key}: ${hexToRgb(value as string)};`).join('\n')}
    ${Object.entries(darkGray).map(([key, value]) => `--raw-gray-${key}: ${hexToRgb(value as string)};`).join('\n')}
  }`
}

export function useTheme(theme: ThemeConfig, locationTitle?: string) {
  const headConfig = {
    title: locationTitle ? locationTitle + ' - Ovatu Manager' : 'Ovatu Manager',
    style: [
      {
        key: 'theme-colors',
        innerHTML: buildStyles(theme),
      },
    ],
  }

  return { headConfig, hexToRgb }
}

/**
 * Reactive version — call from layouts with a getter that reads
 * from reactive stores so the CSS updates when the theme changes.
 *
 * Usage:
 *   useReactiveTheme(
 *     () => ($auth as any).theme,
 *     () => ($auth as any).info?.location?.title,
 *   )
 */
export function useReactiveTheme(
  themeGetter: () => ThemeConfig,
  titleGetter?: () => string | undefined,
) {
  useHead(computed(() => {
    const theme = themeGetter()
    const title = titleGetter?.()
    return {
      title: title ? title + ' - Ovatu Manager' : 'Ovatu Manager',
      style: [
        {
          key: 'theme-colors',
          innerHTML: buildStyles(theme),
        },
      ],
    }
  }))
}
